<template>
    <div class="modal fade" id="cardProfileModal" ref="modalForm" tabindex="-1" aria-labelledby="cardProfileModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <form v-if="profile && activeData" class="modal-content" @submit.prevent="handleSubmit">
                <div class="modal-header text-bg-primary">
                    <h5 class="modal-title" id="cardProfileModalLabel">{{ isEditing ? $t('cardProfile.editTitle') : $t('cardProfile.addTitle') }}</h5>
                    <button type="button" class="btn-close text-light" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-2">
                        <label for="card-profile-type" class="form-label">{{ $t('cardProfile.badgeType') }}</label>
                        <select id="card-profile-type" class="form-select" :value="profile.type" :disabled="isEditing" @change="onTypeChanged($event.target.value)">
                            <option v-for="type in availableTypes" :key="type.value" :value="type.value">
                                {{ type.label }}
                            </option>
                        </select>
                    </div>

                    <div class="mb-2">
                        <label for="card-profile-read-mode" class="form-label">{{ $t('cardProfile.readMode') }}</label>
                        <select id="card-profile-read-mode" class="form-select" v-model="activeData.read_mode">
                            <option v-for="mode in readModes" :key="mode.value" :value="mode.value">
                                {{ mode.label }}
                            </option>
                        </select>
                    </div>

                    <template v-if="profile.type === 'MIFARE' && usesUid">
                        <div class="mb-2">
                            <PasswordComponent
                                id="mifare-app-key"
                                :label="$t('cardProfile.appKey')"
                                v-model="activeData.app_key"
                                autocomplete="off"
                                tabindex="0"
                            />
                        </div>
                        <div class="mb-2">
                            <label for="mifare-key-type" class="form-label">{{ $t('cardProfile.keyType') }}</label>
                            <select id="mifare-key-type" class="form-select" v-model="activeData.key_type">
                                <option value="A">A</option>
                                <option value="B">B</option>
                            </select>
                        </div>
                        <NumberField id="mifare-block-id" :label="$t('cardProfile.block')" v-model:value="activeData.block_id" />
                        <OffsetLengthFields v-model:offset="activeData.offset" v-model:length="activeData.data_length" />
                    </template>

                    <template v-if="profile.type === 'DESFIRE' && usesUid">
                        <div class="mb-2">
                            <label for="desfire-app-id" class="form-label">Application ID</label>
                            <input id="desfire-app-id" class="form-control" v-model.trim="appIdHex" maxlength="6" pattern="[0-9a-fA-F]{1,6}" required />
                        </div>
                        <div class="mb-2">
                            <label for="desfire-encryption" class="form-label">{{ $t('cardProfile.encryption') }}</label>
                            <select id="desfire-encryption" class="form-select" v-model="activeData.encryption">
                                <option value="AES">AES</option>
                                <option value="DES">DES</option>
                                <option value="Clear">{{ $t('cardProfile.clear') }}</option>
                            </select>
                        </div>
                        <div class="mb-2">
                            <PasswordComponent
                                id="desfire-app-key"
                                :label="$t('cardProfile.appKey')"
                                v-model="activeData.app_key"
                                :required="false"
                                autocomplete="off"
                                tabindex="0"
                            />
                        </div>
                        <NumberField id="desfire-key-id" :label="$t('cardProfile.keyNumber')" v-model:value="activeData.key_id" />
                        <NumberField id="desfire-file-id" :label="$t('cardProfile.fileNumber')" v-model:value="activeData.file_id" />
                        <OffsetLengthFields v-model:offset="activeData.offset" v-model:length="activeData.data_length" />
                    </template>

                    <template v-if="profile.type === 'STID_VCARD' && usesUid">
                        <div class="mb-2">
                            <PasswordComponent
                                id="stid-app-key"
                                :label="$t('cardProfile.blueNfcKey')"
                                v-model="activeData.app_key"
                                autocomplete="off"
                                tabindex="0"
                            />
                        </div>
                        <div class="mb-2">
                            <label for="stid-config-name" class="form-label">{{ $t('cardProfile.configName') }}</label>
                            <input id="stid-config-name" class="form-control" v-model.trim="activeData.config_name" maxlength="32" required />
                        </div>
                        <OffsetLengthFields v-model:offset="activeData.offset" v-model:length="activeData.data_length" />
                        <div class="mb-2">
                            <label for="stid-site-code" class="form-label">{{ $t('cardProfile.siteCode') }}</label>
                            <input id="stid-site-code" class="form-control" v-model.trim="siteCodeHex" maxlength="4" pattern="[0-9a-fA-F]{1,4}" required />
                        </div>
                    </template>

                    <div v-if="activeData.read_mode !== 'NotUse' && activeData.reverse_order !== undefined" class="d-sm-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2 mb-2">
                        <label class="form-check-label flex-grow-1 mb-2 mb-sm-0 me-2" for="card-profile-reverse-order">{{ $t('cardProfile.reverseOrder') }}</label>
                        <input type="checkbox" id="card-profile-reverse-order" v-model="activeData.reverse_order" data-switch="bool">
                        <label for="card-profile-reverse-order" data-on-label="Oui" data-off-label="Non"></label>
                    </div>

                    <div v-if="messageError" class="custom-error-msg">
                        {{ messageError }}
                    </div>

                    <div class="mt-3 d-flex justify-content-center align-items-center">
                        <button type="button" class="btn btn-outline-secondary me-2" style="min-width: 140px;" ref="closeButton" data-bs-dismiss="modal">{{ $t('cardProfile.cancel') }}</button>
                        <button type="submit" class="btn btn-primary" style="min-width: 140px;">OK</button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { computed, defineComponent, h, ref } from 'vue';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);
import PasswordComponent from '@/components/password.vue';
import CardProfile from '@/models/cardProfile';

const NumberField = defineComponent({
    props: {
        id: String,
        label: String,
        value: Number,
    },
    emits: ['update:value'],
    setup(props, { emit }) {
        return () => h('div', { class: 'mb-2' }, [
            h('label', { class: 'form-label', for: props.id }, props.label),
            h('input', {
                id: props.id,
                class: 'form-control',
                type: 'number',
                min: 0,
                max: 255,
                step: 1,
                value: props.value,
                onInput: event => emit('update:value', Number(event.target.value)),
            }),
        ]);
    },
});

const OffsetLengthFields = defineComponent({
    props: {
        offset: Number,
        length: Number,
    },
    emits: ['update:offset', 'update:length'],
    setup(props, { emit }) {
        return () => h('div', { class: 'row' }, [
            h('div', { class: 'col-md-6' }, [
                h(NumberField, {
                    id: 'card-profile-offset',
                    label: t('cardProfile.offset'),
                    value: props.offset,
                    'onUpdate:value': value => emit('update:offset', value),
                }),
            ]),
            h('div', { class: 'col-md-6' }, [
                h(NumberField, {
                    id: 'card-profile-length',
                    label: t('cardProfile.length'),
                    value: props.length,
                    'onUpdate:value': value => emit('update:length', value),
                }),
            ]),
        ]);
    },
});

export default {
    name: 'CardProfileModal',
    components: {
        NumberField,
        OffsetLengthFields,
        PasswordComponent,
    },
    emits: ['cardProfileSaved'],
    setup(props, { emit }) {
        const closeButton = ref(null);
        const profile = ref(null);
        const isEditing = ref(false);
        const existingProfiles = ref([]);
        const messageError = ref('');

        const readModes = [
            { value: 'CsnOnly', label: t('cardProfile.readModes.csnOnly') },
            { value: 'UidOrCsn', label: t('cardProfile.readModes.uidOrCsn') },
            { value: 'UidOnly', label: t('cardProfile.readModes.uidOnly') },
            { value: 'NotUse', label: t('cardProfile.readModes.notUse') },
        ];

        const typeLabels = {
            HAXO: 'HAXO SECURE',
            MIFARE: 'MIFARE',
            DESFIRE: 'DESFIRE',
            STID_VCARD: 'STid Mobile ID',
        };

        const getSelectableTypes = () => {
            const existingTypes = existingProfiles.value.map(item => item.type);
            let types = ['HAXO', 'MIFARE', 'DESFIRE', 'STID_VCARD'];
            if(existingTypes.includes('HAXO')) {
                types = types.filter(type => !['HAXO', 'DESFIRE'].includes(type));
            }
            if(existingTypes.includes('MIFARE')) {
                types = types.filter(type => type !== 'MIFARE');
            }
            if(existingTypes.includes('DESFIRE')) {
                types = types.filter(type => !['DESFIRE', 'HAXO'].includes(type));
            }
            if(existingTypes.includes('STID_VCARD')) {
                types = types.filter(type => type !== 'STID_VCARD');
            }
            return types;
        };

        const availableTypes = computed(() => {
            if(!profile.value) return [];
            if(isEditing.value) {
                return [{ value: profile.value.type, label: typeLabels[profile.value.type] }];
            }

            return getSelectableTypes().map(type => ({ value: type, label: typeLabels[type] }));
        });

        const activeData = computed(() => {
            switch(profile.value?.type) {
                case 'DESFIRE':
                    return profile.value.desfire_card_profile;
                case 'HAXO':
                    return profile.value.haxo_card_profile;
                case 'STID_VCARD':
                    return profile.value.stid_vcard_profile;
                default:
                    return profile.value?.mifare_card_profile;
            }
        });

        const usesUid = computed(() => activeData.value?.read_mode?.includes('Uid'));

        // Keep the value as text while the user is editing it. Converting it to a
        // number on every keystroke would reformat the input and move the cursor.
        const appIdHex = ref('');

        const resetAppIdHex = () => {
            appIdHex.value = Number(activeData.value?.app_id ?? 0).toString(16).padStart(6, '0').toUpperCase();
        };

        const siteCodeHex = computed({
            get() {
                return Number(activeData.value?.site_code ?? 0).toString(16).padStart(4, '0').toUpperCase();
            },
            set(value) {
                activeData.value.site_code = parseInt(value || '0', 16);
            },
        });

        const setProfile = (selectedProfile, profiles) => {
            existingProfiles.value = profiles;
            messageError.value = '';
            isEditing.value = Boolean(selectedProfile);
            if(selectedProfile) {
                profile.value = selectedProfile.clone();
            } else {
                const firstType = getSelectableTypes()[0] || 'MIFARE';
                profile.value = new CardProfile(firstType);
            }
            resetAppIdHex();
        };

        const onTypeChanged = (type) => {
            // Commit the app id text buffer before leaving DESFIRE so the value
            // is restored if the user comes back, like every other field.
            if(profile.value.type === 'DESFIRE' && /^[0-9a-fA-F]+$/.test(appIdHex.value)) {
                activeData.value.app_id = parseInt(appIdHex.value, 16);
            }
            profile.value.type = type;
            resetAppIdHex();
        };

        const validateHex = (value, required, label) => {
            if(!value && !required) return true;
            if(!value || !/^[0-9a-fA-F]+$/.test(value)) {
                messageError.value = t('cardProfile.errors.hexInvalid', { label });
                return false;
            }
            return true;
        };

        const validate = () => {
            messageError.value = '';
            if(usesUid.value) {
                if(profile.value.type === 'MIFARE' && !validateHex(activeData.value.app_key, true, t('cardProfile.errors.appKeyLabel'))) return false;
                if(profile.value.type === 'DESFIRE') {
                    if(!validateHex(appIdHex.value, true, t('cardProfile.errors.appIdLabel'))) return false;
                    if(!validateHex(activeData.value.app_key, false, t('cardProfile.errors.appKeyLabel'))) return false;
                }
                if(profile.value.type === 'STID_VCARD') {
                    if(!validateHex(activeData.value.app_key, true, t('cardProfile.errors.appKeyLabel'))) return false;
                    if(!activeData.value.config_name?.trim()) {
                        messageError.value = t('cardProfile.errors.configNameRequired');
                        return false;
                    }
                    if(!/^[0-9a-fA-F]{1,4}$/.test(siteCodeHex.value)) {
                        messageError.value = t('cardProfile.errors.siteCodeRange');
                        return false;
                    }
                }
            }
            return true;
        };

        const handleSubmit = () => {
            if(!validate()) return;
            if(profile.value.type === 'DESFIRE' && usesUid.value) {
                activeData.value.app_id = parseInt(appIdHex.value, 16);
            }
            emit('cardProfileSaved', profile.value);
            closeButton.value.click();
        };

        return {
            closeButton,
            profile,
            isEditing,
            readModes,
            availableTypes,
            activeData,
            usesUid,
            appIdHex,
            siteCodeHex,
            messageError,
            setProfile,
            onTypeChanged,
            handleSubmit,
        };
    },
};
</script>

<style scoped>
.custom-error-msg {
  color: red;
  font-size: 14px;
}
</style>
